/**
 * Load something once, with the three states a screen actually has.
 *
 * ⚠️ `loading | error | data` AND NOT `data | undefined`. A screen that only distinguishes "have it"
 * from "don't" renders its empty state during the first load — so a customer with twenty sites is
 * shown "You have no sites yet. Create one" for the length of a cold Cloud Run start, on the screen
 * they land on. That is the specific failure this hook exists to make impossible, and it is why
 * `min-instances=0` (CLAUDE.md §5.2) is survivable.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiFailure } from './api.ts'

export type Loaded<T> =
  | { state: 'loading' }
  | { state: 'error'; failure: ApiFailure }
  | { state: 'ready'; data: T }

export function useLoad<T>(load: (signal: AbortSignal) => Promise<T>, deps: unknown[] = []): [Loaded<T>, () => void] {
  const [result, setResult] = useState<Loaded<T>>({ state: 'loading' })
  const [nonce, setNonce] = useState(0)
  const loadRef = useRef(load)
  loadRef.current = load

  useEffect(() => {
    const controller = new AbortController()
    let live = true
    setResult({ state: 'loading' })

    loadRef.current(controller.signal).then(
      (data) => {
        if (live) setResult({ state: 'ready', data })
      },
      (error: unknown) => {
        // An abort is this component going away, not a failure to show anybody.
        if (!live || (error as Error).name === 'AbortError') return
        setResult({
          state: 'error',
          failure: error instanceof ApiFailure ? error : new ApiFailure('unknown', { en: 'Something went wrong.', ar: 'حدث خطأ ما.' }, 0),
        })
      },
    )

    return () => {
      live = false
      controller.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce])

  return [result, useCallback(() => setNonce((n) => n + 1), [])]
}
